import { Tag } from '@octanner/prism-core'
import { ComponentProps } from 'react'
import { CheckResponsive, Rotate, Alert, Close } from '@octanner/prism-icons'
import { tannerGray } from '@octanner/prism-core/ThemeProvider/colors'
import { ScheduledTask } from 'library/src/hooks/useGetCelebrationByIdentity'
import { DateTime } from 'luxon'

export const formatDateString = (dateStr: string): string => {
  return DateTime.fromISO(dateStr, { zone: 'local' }).toFormat('dd LLL yyyy')
}
export const Status = {
  Cancelled: 'Cancelled',
  Failed: 'Failed',
  Pending: 'Pending',
  Scheduled: 'Scheduled',
  Completed: 'Completed',
  ToBeOmitted: 'ToBeOmitted',
  Omitted: 'Omitted',
  NotExecuted: 'NotExecuted',
} as const

export type Status = (typeof Status)[keyof typeof Status]

export const StatusLabel: Record<Status, string> = {
  [Status.Cancelled]: 'Cancelled',
  [Status.Failed]: 'Failed',
  [Status.Pending]: 'Pending',
  [Status.Scheduled]: 'Scheduled',
  [Status.Completed]: 'Completed',
  [Status.ToBeOmitted]: 'To Be Omitted',
  [Status.Omitted]: 'Omitted',
  [Status.NotExecuted]: 'Not Executed',
} as const

export const StatusPriority = {
  [Status.Cancelled]: 1,
  [Status.Failed]: 2,
  [Status.Pending]: 3,
  [Status.Scheduled]: 4,
  [Status.ToBeOmitted]: 5,
  [Status.Omitted]: 6,
  [Status.NotExecuted]: 7,
  [Status.Completed]: 8,
} as const

export const getStatusProps = (
  status: Status
): {
  tagVariant: ComponentProps<typeof Tag>['variant']
  icon: typeof CheckResponsive
  timelineDotColor: string
  timelineConnectorColor: string
} => {
  switch (status) {
    case Status.Completed:
      return {
        tagVariant: 'success',
        icon: CheckResponsive,
        timelineDotColor: '#07702A',
        timelineConnectorColor: '#07702A',
      }
    case Status.Failed:
      return {
        tagVariant: 'error',
        icon: Alert,
        timelineDotColor: '#D10A12',
        timelineConnectorColor: '#D10A12',
      }
    case Status.Pending:
      return {
        tagVariant: 'warning',
        icon: Rotate,
        timelineDotColor: '#cB6327',
        timelineConnectorColor: '#cB6327',
      }
    case Status.Scheduled:
      return {
        tagVariant: 'info',
        icon: null,
        timelineDotColor: tannerGray[400],
        timelineConnectorColor: tannerGray[400],
      }

    case Status.ToBeOmitted:
      return {
        tagVariant: 'archived',
        icon: null,
        timelineDotColor: tannerGray[400],
        timelineConnectorColor: tannerGray[400],
      }
    case Status.Omitted:
      return {
        tagVariant: 'archived',
        icon: CheckResponsive,
        timelineDotColor: tannerGray[400],
        timelineConnectorColor: tannerGray[400],
      }
    case Status.NotExecuted:
      return {
        tagVariant: 'archived',
        icon: Close,
        timelineDotColor: tannerGray[400],
        timelineConnectorColor: tannerGray[400],
      }
    case Status.Cancelled:
      return {
        tagVariant: 'archived',
        icon: Close,
        timelineDotColor: tannerGray[700],
        timelineConnectorColor: tannerGray[700],
      }
    default:
      return {
        tagVariant: 'archived',
        icon: null,
        timelineDotColor: tannerGray[400],
        timelineConnectorColor: tannerGray[400],
      }
  }
}

export const getScheduleTaskStatus = (
  scheduledTask: ScheduledTask,
  actionRespectsPrivacy: boolean,
  hasPrivateCelebrations: boolean
): Status | null => {
  const now = new Date()
  const startTime = new Date(scheduledTask.startTime)
  const endTime = new Date(scheduledTask.endTime)
  if (scheduledTask.cancelledTime) return Status.Cancelled
  if (
    startTime < now &&
    now < endTime &&
    !scheduledTask.lastExecution?.executionTime
  )
    return Status.Pending
  if (scheduledTask.lastExecution?.status == 'FAILED') return Status.Failed
  if (scheduledTask.lastExecution?.status == 'SUCCESS') return Status.Completed
  if (scheduledTask.lastExecution?.status == 'OMITTED') return Status.Omitted
  if (
    now < startTime &&
    !scheduledTask.lastExecution &&
    actionRespectsPrivacy &&
    hasPrivateCelebrations
  )
    return Status.ToBeOmitted
  if (now < startTime && !scheduledTask.lastExecution) return Status.Scheduled
  if (endTime < now && !scheduledTask.lastExecution) return Status.NotExecuted
  return null
}

export const getHighestPriorityTask = (
  scheduledTaskWithStatuses: (ScheduledTask & { status: Status })[]
) => {
  if (scheduledTaskWithStatuses.length === 1) {
    return scheduledTaskWithStatuses[0]
  } else if (scheduledTaskWithStatuses.length === 2) {
    const [first, second] = scheduledTaskWithStatuses
    return StatusPriority[first.status] < StatusPriority[second.status]
      ? first
      : second
  }
  throw new Error('Expected at least one scheduled task')
}

export const getDisplayDataForScheduledTask = <T extends ScheduledTask>(
  scheduledTask: T
) =>
  scheduledTask.lastExecution?.executionTime
    ? formatDateString(scheduledTask.lastExecution.executionTime)
    : scheduledTask.cancelledTime
    ? formatDateString(scheduledTask.cancelledTime)
    : formatDateString(scheduledTask.startTime)

export const getPrivacyTooltipText = (status: Status) => {
  switch (status) {
    case Status.ToBeOmitted:
      return 'This action will be omitted in the celebration because celebration settings are turned to private for the employee.'
    case Status.Omitted:
      return 'This action was not included in the celebration because celebration settings are turned to private for the employee.'
    default:
      return 'This employee has their settings turned to private, causing any actions in the timeline that are marked with the privacy tag to be omitted from the celebration.'
  }
}
